/*
  Entrar no Mémoire pela sessão da plataforma.

  Este é o passo 5 do login único visto do lado do navegador. O servidor já
  aceita o token do Supabase desde 20/09 (`api/auth_supabase.php`); o que
  faltava era o front **mandar** esse token em vez de pedir e-mail e senha.

  ---

  **A sessão vem do cookie, não daqui.** Quem entra é a plataforma, em
  `frances.uninativ.com`, e grava a sessão num cookie de escopo `.uninativ.com`.
  Este subdomínio lê o mesmo cookie. O adaptador é cópia verbatim do dela —
  veja `sessao-no-cookie.ts` e `scripts/copia-a-sessao.mjs`.

  **Por que o SDK, e não ler o cookie na mão.** O `access_token` vale uma hora.
  Lido cru e guardado, o aluno estudaria cinquenta minutos e começaria a tomar
  401 no meio da revisão — que apareceria como "o Mémoire desloga sozinho", e
  não como "o token venceu". O SDK renova antes de vencer e reescreve o cookie;
  nós só espelhamos o valor novo.

  **Por que espelhar em `localStorage.auth_token`.** Doze lugares deste código
  leem essa chave direto, de forma síncrona, dentro de objetos de cabeçalho.
  Trocar os doze por uma chamada assíncrona seria um refactor grande numa
  semana de migração. Espelhar mantém todos funcionando sem alteração, e o
  espelho fica fresco porque `onAuthStateChange` dispara a cada renovação.

  **Desligado por padrão.** `francesnativo.com/memoire` roda este mesmo código,
  tem dezessete alunos de verdade e não tem plataforma na frente. Sem as três
  variáveis, nada aqui acontece e o login próprio continua sendo o único
  caminho.
*/
import { createClient, type SupabaseClient, type Session } from '@supabase/supabase-js'
import { sessaoNoCookie } from './sessao-no-cookie'

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined
const chave = import.meta.env.VITE_SUPABASE_CHAVE as string | undefined

/*
  Três condições, e a terceira é explícita de propósito.

  Derivar o "ligado" só da presença das credenciais faria uma variável
  acrescentada por engano em produção desligar o login de dezessete pessoas. Um
  interruptor com nome próprio custa uma linha no `.env` e não tem esse modo de
  falha.
*/
export const loginUnicoLigado = Boolean(
  url && chave && import.meta.env.VITE_LOGIN_UNICO === '1',
)

/*
  Entrou na plataforma, mas não tem Mémoire.

  Não é erro nem sessão inválida: é uma pessoa de verdade, autenticada, sem
  este produto — que é vendido à parte. Sem distinguir os dois casos ela seria
  devolvida para a plataforma sem explicação, voltaria, e seria devolvida de
  novo. A tela precisa saber a diferença para poder dizê-la.
*/
let semCadastro = false
export const semCadastroNoMemoire = (): boolean => semCadastro

/** Para onde mandar quem chega sem sessão. */
export const ENDERECO_DA_PLATAFORMA =
  (import.meta.env.VITE_URL_PLATAFORMA as string | undefined) ||
  'https://frances.uninativ.com'

/**
 * A entrada, com o caminho de volta para cá.
 *
 * A plataforma devolve para `volta` assim que a pessoa entra. Sem isso, um
 * aluno só do Mémoire cairia no portão do curso ("acesso não liberado"), que é
 * verdade sobre o curso e nada diz sobre o Mémoire.
 */
export function entradaComVolta(): string {
  const destino = new URL(ENDERECO_DA_PLATAFORMA)
  destino.searchParams.set('volta', location.href)
  destino.hash = '#entrar'
  return destino.toString()
}

const cliente: SupabaseClient | null = loginUnicoLigado
  ? createClient(url!, chave!, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        /* O Mémoire nunca recebe link mágico: quem autentica é a plataforma.
           Ligado, isto tentaria consumir um token da URL que não existe. */
        detectSessionInUrl: false,
        storage: sessaoNoCookie,
      },
    })
  : null

/** Espelha o token no lugar que o resto do código já lê. */
function espelhar(sessao: Session | null): void {
  if (sessao?.access_token) {
    localStorage.setItem('auth_token', sessao.access_token)
  } else {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
  }
}

if (cliente) {
  /* Dispara no arranque, a cada renovação e no encerramento. É ele que mantém
     o espelho fresco depois da primeira hora. */
  cliente.auth.onAuthStateChange((_evento, sessao) => espelhar(sessao))
}

/**
 * Prepara a sessão antes da primeira tela.
 *
 * Devolve `true` quando há alguém autenticado. O `App` espera por isto: sem a
 * espera, o primeiro render aconteceria antes de o cookie ter sido lido, os
 * guardas veriam `localStorage` vazio e mandariam todo mundo para o login —
 * um piscar de tela de login em quem já estava logado.
 */
export async function prepararSessao(): Promise<boolean> {
  if (!cliente) return Boolean(localStorage.getItem('auth_token'))

  const { data } = await cliente.auth.getSession()
  espelhar(data.session)
  if (!data.session) return false

  /*
    Quem é esta pessoa aqui dentro — perguntado ao Mémoire, não ao token.

    O token diz o e-mail; o `tipo_usuario` e o `id` são deste produto, e é por
    eles que os guardas de rota decidem. Quem tem conta na plataforma e não tem
    cadastro aqui recebe 401, e o resultado é o mesmo de nunca ter entrado: o
    Mémoire é vendido à parte.
  */
  try {
    const r = await fetch(
      `${import.meta.env.VITE_API_URL || '/api'}/auth.php?action=me&_t=${Date.now()}`,
      { headers: { Authorization: `Bearer ${data.session.access_token}` } },
    )
    if (!r.ok) {
      /* 401 com sessão boa quer dizer exatamente uma coisa: o token vale, e
         não há cadastro deste produto por trás dele. */
      semCadastro = r.status === 401
      espelhar(null)
      return false
    }
    const corpo = await r.json()
    if (!corpo?.user) {
      espelhar(null)
      return false
    }
    semCadastro = false
    localStorage.setItem('user', JSON.stringify(corpo.user))
    return true
  } catch {
    /* Rede fora no arranque não deve apagar a sessão: quem já tinha `user`
       guardado continua, e a primeira chamada de verdade dirá se vale. */
    return Boolean(localStorage.getItem('user'))
  }
}

/**
 * O token em vigor, renovado se já venceu. Nulo sem login único ou sem sessão.
 *
 * O SDK renova sozinho enquanto a aba está aberta e em primeiro plano. No
 * celular não está: o aluno bloqueia a tela no meio da revisão, volta uma hora
 * depois, toca em "Fácil" — e o pedido sai com o token vencido antes de o SDK
 * acordar. A API responde 401, e isso aparecia como "erro ao registrar a
 * resposta". `getSession()` renova quando o token venceu; quem chama repete o
 * pedido com o novo (ver `fetchAPI`).
 */
export async function tokenRenovado(): Promise<string | null> {
  if (!cliente) return null
  try {
    const { data } = await cliente.auth.getSession()
    espelhar(data.session)
    return data.session?.access_token ?? null
  } catch {
    return null
  }
}

/**
 * Sair de verdade.
 *
 * Com login único, sair do Mémoire e continuar na plataforma seria mentira: a
 * próxima visita leria o mesmo cookie e entraria de novo. O `signOut` encerra
 * a sessão compartilhada — que é o que "sair" significa quando ela é uma só.
 */
export async function sairDoLoginUnico(): Promise<void> {
  if (!cliente) return
  await cliente.auth.signOut()
}

/*
  Os sistemas do Francês Nativo, para o atalho do topo
  (`components/SeletorDeSistemas.tsx`).

  Quem tem o quê responde o banco (`plataforma.meus_sistemas`), com a sessão do
  login único. Sem login único — o Mémoire antigo de `francesnativo.com` — não
  há atalho: devolve `null` e o botão nem aparece.
*/
export type Sistema = {
  codigo: string
  nome: string
  descricao: string
  url: string
  tem_acesso: boolean
  ordem: number
}

export async function meusSistemas(): Promise<Sistema[] | null> {
  if (!cliente) return null
  const { data, error } = await cliente.schema('plataforma').rpc('meus_sistemas')
  if (error) return []
  return (data as Sistema[] | null) ?? []
}

/** O e-mail de quem está conectado, para o topo do atalho. */
export async function emailConectado(): Promise<string | null> {
  if (!cliente) return null
  const { data } = await cliente.auth.getSession()
  return data.session?.user.email ?? null
}
